import React, { useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import {
  accumulateNodeUpdates,
  downloadUrlMapForTree,
  getDataSizeTotal,
  httpAndDataUrls,
  humanFileSize,
  sortImageUrls,
  replaceDataSrcsByS3imgs,
  getNodeReplacementsForExisting,
  verifyMimeTypes,
} from './utility'
import { INode } from 'shared/types/tree'
import { downloadFile } from 'shared/utils/fileExport'
import { getCurriculaContentFolderForTree } from '../ingestion/Utility'
import { ConfirmCommitButton } from 'shared/ui'
import { updateAll } from '../tree/nodeUpdates/utility'
import {
  ImageVerificationContext,
  useImageVerificationContext,
} from './context'

export function ImageVerificationPreview({
  tree,
  didUpdateTree,
}: {
  tree: INode
  didUpdateTree: () => void
}) {
  const [data, setData] = React.useState<string[]>([])
  const [externalUrls, setExternalUrls] = React.useState<string[]>([])
  const [internalUrls, setInternalUrls] = React.useState<string[]>([])
  const [fileSpace, setFileSpace] = React.useState('')

  React.useEffect(() => {
    const { externalUrls, data, internalUrls } = httpAndDataUrls(tree)
    setData(data)
    setExternalUrls(externalUrls)
    setInternalUrls(internalUrls)
    setFileSpace(humanFileSize(getDataSizeTotal(...data)))
  }, [tree])

  const contextData = useMemo(() => {
    return {
      tree,
      externalUrls,
      internalUrls,
      dataSrcs: data,
      onTreeUpdate: didUpdateTree,
    }
  }, [tree, externalUrls, internalUrls, data, didUpdateTree])

  return (
    <ImageVerificationContext.Provider value={contextData}>
      <Box>
        {!!data.length && (
          <>
            <Box>{`${data.length} data srcs taking ${fileSpace}`}</Box>
            <DataSrcToImageUpdateButton />
          </>
        )}
        {!!externalUrls.length && (
          <Box>
            {`${externalUrls.length} external srcs`}
            <Button onClick={() => downloadUrlMapForTree(tree)}>
              Get Url Map
            </Button>
            <ExternalImageUpdateButton />
          </Box>
        )}
        {!![...internalUrls, ...externalUrls].length && <MimeTypeButton />}
      </Box>
    </ImageVerificationContext.Provider>
  )
}

function MimeTypeButton() {
  const [badMimeType, setBadMimeType] = useState<string[]>()
  const { externalUrls, internalUrls } = useImageVerificationContext()

  const doIt = async () => {
    const withIssue = await verifyMimeTypes(
      ...[...externalUrls, ...internalUrls],
    )
    setBadMimeType(withIssue)
  }

  const mimeTypeExport = () => {
    if (!badMimeType) return
    const dataString = badMimeType.map((u) => [u, u].join(',')).join('\n')
    downloadFile({ fileName: 'mimefixs.csv', fileDataString: dataString })
  }

  if (badMimeType) {
    return (
      <Button
        onClick={mimeTypeExport}
      >{`Export ${badMimeType.length} Mime Types`}</Button>
    )
  }

  return (
    <ConfirmCommitButton disabled={!!badMimeType} onClick={doIt}>
      Verify Mimes
    </ConfirmCommitButton>
  )
}

function DataSrcToImageUpdateButton() {
  const { tree, dataSrcs, onTreeUpdate } = useImageVerificationContext()
  const onClickReplace = async () => {
    await replaceDataSrcsByS3imgs(dataSrcs, tree)
    onTreeUpdate()
  }

  return (
    <ConfirmCommitButton variant="contained" onClick={onClickReplace}>
      Replace Data Srcs
    </ConfirmCommitButton>
  )
}

function ExternalImageUpdateButton() {
  const [disabled, setDisabled] = React.useState(false)
  const [canFix, setCanFix] = React.useState<string[]>([])
  const [existingImages, setExistingImages] = React.useState<{
    [key: string]: string
  }>()

  const { tree, externalUrls, onTreeUpdate } = useImageVerificationContext()

  React.useEffect(() => {
    getCurriculaContentFolderForTree(tree).then((e) => {
      setExistingImages(e)
    })
  }, [tree])

  React.useEffect(() => {
    if (!existingImages) return
    const { canFix } = sortImageUrls(externalUrls, existingImages)
    setCanFix(canFix)
  }, [existingImages, externalUrls])

  if (!existingImages || !canFix.length) return null

  function doIt() {
    if (!existingImages) return
    setDisabled(true)
    const updates = accumulateNodeUpdates(
      getNodeReplacementsForExisting({
        tree,
        externalUrls: canFix,
        existingByName: existingImages,
      }),
    )
    updateAll(...updates).then(onTreeUpdate)
  }

  return (
    <ConfirmCommitButton onClick={doIt} disabled={!canFix.length || disabled}>
      <>Url Swap ({canFix.length})</>
    </ConfirmCommitButton>
  )
}
